// services/moduleSelector.js
// Deterministic module picker - pure function, no AI inference
// Uses time, session context, and weighted random to select which DJ module to use
import logger from './logger';

/**
 * Get time-of-day category
 * @returns {'morning'|'afternoon'|'evening'|'late-night'}
 */
export function getTimeOfDay() {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 22) return 'evening';
  return 'late-night';
}

/**
 * Select a module based on deterministic local logic
 *
 * @param {Object} context
 * @param {string} context.timeOfDay - 'morning'|'afternoon'|'evening'|'late-night'
 * @param {number} context.sessionMinutes - Minutes since radio mode activated
 * @param {Array} context.availableModules - Module metadata from GET /api/modules
 * @param {string|null} context.lastModuleId - Last used module ID (for anti-repeat)
 * @returns {string|null} module_id to use, or null if no eligible modules
 */
export function selectModule(context) {
  const {
    timeOfDay = getTimeOfDay(),
    sessionMinutes = 0,
    availableModules = [],
    lastModuleId = null
  } = context;

  // Filter to live modules only (pool modules are fetched directly)
  const liveModules = availableModules.filter(m => m.category === 'live');

  if (liveModules.length === 0) return null;

  // Filter by conditions
  const eligible = liveModules.filter(m => {
    const cond = m.conditions || {};

    // Time of day filter
    if (cond.time_of_day && !cond.time_of_day.includes(timeOfDay)) {
      return false;
    }

    // Session duration filter
    if (cond.min_session_minutes && sessionMinutes < cond.min_session_minutes) {
      return false;
    }

    // Skip zero-weight modules (song_intro, show_open - used explicitly)
    if (m.weight === 0) {
      return false;
    }

    return true;
  });

  if (eligible.length === 0) return null;

  // Weighted random selection with anti-repeat
  const weights = eligible.map(m => {
    let weight = m.weight || 10;
    // Reduce weight of last-used module
    if (m.id === lastModuleId) {
      weight = Math.max(2, Math.floor(weight / 3));
    }
    return weight;
  });

  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  let r = Math.floor(Math.random() * totalWeight) + 1;
  let cumulative = 0;

  for (let i = 0; i < eligible.length; i++) {
    cumulative += weights[i];
    if (r <= cumulative) {
      logger.debug(`Module selected: ${eligible[i].id} (weight=${weights[i]}, eligible=${eligible.length})`);
      return eligible[i].id;
    }
  }

  return eligible[0].id; // Fallback
}

export default { selectModule, getTimeOfDay };
